"use client";

import React, { useMemo, useRef, useCallback } from "react";
import styled, { css } from "styled-components";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { useDateListStore } from "@/lib/stores/dateListStore";
import { formatPriceWithDecimals } from "@/lib/Helpers/formatPrice";
import initialState from "@/lib/StaticData/searchFormData";
import { MealTypeIds } from "@/lib/Types/searchForm";
import { Text } from "@lmn/design-system";
import { channel } from "@/lib/Types/Channel";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import media from "@/styles/mediaQueries";
import { AppConfig } from "@/lib/Types/AppConfig";

const TabsContainer = styled.div``;

const TabList = styled.ul`
  display: none;
  list-style: none;
  padding: 0;
  margin: 0;
  table-layout: fixed;
  width: 100%;
  ${media.screenMdMin`
      display: table;
    `};
`;

const selectedTabCss = css`
  border-bottom: 4px solid
    ${({ theme }) =>
      theme.kallisto.color.border.interactive.borderPrimaryDefault};
  color: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryDefault};

  p {
    color: ${({ theme }) =>
      theme.kallisto.color.text.interactive.textPrimaryDefault};
  }
`;

const disabledTabCss = css`
  opacity: 0.3;
  cursor: default;
`;

const hoverTabCss = css`
  background-color: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundNeutralHover};
`;

const focusTabCss = css`
  outline: 2px solid
    ${({ theme }) =>
      theme.kallisto.color.border.interactive.borderPrimaryDefault};
  outline-offset: -2px;
`;

const TabItem = styled.li<{ $isSelected: boolean; $isDisabled: boolean }>`
  border-bottom: 4px solid transparent;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  cursor: ${({ $isDisabled }) => ($isDisabled ? "default" : "pointer")};
  display: table-cell;
  text-align: center;
  vertical-align: middle;
  padding: 8px;

  p {
    color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  }

  &:hover {
    ${({ $isSelected, $isDisabled }) =>
      !$isDisabled && !$isSelected && hoverTabCss}
  }

  &:focus-visible {
    ${({ $isDisabled }) => !$isDisabled && focusTabCss}
  }

  ${({ $isSelected }) => $isSelected && selectedTabCss}
  ${({ $isDisabled }) => $isDisabled && disabledTabCss}
`;

const TabLabel = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
`;

const TabSubLabel = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
`;

interface Tab {
  id: string;
  label: string;
  subLabel: string;
  isSelected: boolean;
  isDisabled: boolean;
}

interface MealTypeTabsProps {
  appConfig: AppConfig;
}

export default function MealTypeTabs({ appConfig }: MealTypeTabsProps) {
  const { viewMode } = appConfig;
  const searchParams = useReactiveSearchParams();
  const { updateFilter } = useFilterState();
  const priceList = useDateListStore((state) => state.priceList);
  const tabRefs = useRef<(HTMLLIElement | null)[]>([]);

  const selectedMealType = searchParams.get("mealType");

  const priceMap = useMemo((): Record<number, number> => {
    if (!priceList) return {};
    return priceList.reduce<Record<number, number>>((acc, item) => {
      acc[item.mealCode] = item.minTotalPrice;
      return acc;
    }, {});
  }, [priceList]);

  const lowestPrice = useMemo(() => {
    if (!priceList || priceList.length === 0) return null;
    return Math.min(...priceList.map((p) => p.minTotalPrice));
  }, [priceList]);

  const tabs: Tab[] = useMemo(() => {
    const mealType = initialState.mealType;

    return [
      {
        id: "0",
        label: "alle Angebote",
        subLabel: lowestPrice ? formatPriceWithDecimals(lowestPrice) : "_",
        isSelected: !selectedMealType || selectedMealType === "0",
        isDisabled: false,
      },
      ...mealType.allIds
        .filter((id): id is MealTypeIds => id !== false)
        .map((id) => ({
          id: id.toString(),
          label: mealType.byId[id].label,
          subLabel: priceMap[id]
            ? formatPriceWithDecimals(priceMap[id])
            : "Nicht verfügbar",
          isSelected: selectedMealType === id.toString(),
          isDisabled: !priceMap[id],
        })),
    ];
  }, [priceMap, lowestPrice, selectedMealType]);

  const handleTabClick = useCallback(
    (tab: Tab) => {
      if (tab.isDisabled) return;
      const value = tab.id === "0" ? null : tab.id;
      updateFilter("mealType", value);
    },
    [updateFilter]
  );

  const findNextEnabledTab = useCallback(
    (currentIndex: number, direction: 1 | -1): number => {
      let nextIndex = currentIndex + direction;
      const length = tabs.length;

      while (nextIndex !== currentIndex) {
        if (nextIndex < 0) nextIndex = length - 1;
        if (nextIndex >= length) nextIndex = 0;

        if (!tabs[nextIndex].isDisabled) {
          return nextIndex;
        }
        nextIndex += direction;
      }
      return currentIndex;
    },
    [tabs]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLIElement>, index: number) => {
      let newIndex: number | null = null;

      switch (event.key) {
        case "ArrowRight":
          event.preventDefault();
          newIndex = findNextEnabledTab(index, 1);
          break;
        case "ArrowLeft":
          event.preventDefault();
          newIndex = findNextEnabledTab(index, -1);
          break;
        case "Home":
          event.preventDefault();
          newIndex = findNextEnabledTab(-1, 1);
          break;
        case "End":
          event.preventDefault();
          newIndex = findNextEnabledTab(tabs.length, -1);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          if (!tabs[index].isDisabled) {
            handleTabClick(tabs[index]);
          }
          return;
        default:
          return;
      }

      if (newIndex !== null && tabRefs.current[newIndex]) {
        tabRefs.current[newIndex]?.focus();
      }
    },
    [findNextEnabledTab, tabs, handleTabClick]
  );

  return (
    <>
      <TabsContainer data-testid="meal-type-tabs">
        <Text
          variant="Title1"
          forceTag="h2"
          margin={{ bottom: "space100" }}
          id="all-offers"
        >
          Angeboten
        </Text>
        <Text
          variant="BodySmall"
          color="textSecondary"
          margin={{ bottom: "space300" }}
        >
          {viewMode.channel === channel.HOTEL
            ? "Hotel-Angebote"
            : "Pauschalreise-Angebote"}{" "}
          ab {lowestPrice ? formatPriceWithDecimals(lowestPrice) : "_"}
        </Text>
        <TabList role="tablist" aria-label="Verpflegungsart Filter">
          {tabs.map((tab, index) => (
            <TabItem
              key={tab.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              role="tab"
              id={`meal-tab-${tab.id}`}
              aria-selected={tab.isSelected}
              aria-disabled={tab.isDisabled}
              tabIndex={tab.isSelected ? 0 : -1}
              $isSelected={tab.isSelected}
              $isDisabled={tab.isDisabled}
              onClick={() => handleTabClick(tab)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-value={tab.id}
            >
              <TabLabel>{tab.label}</TabLabel>
              <TabSubLabel>{tab.subLabel}</TabSubLabel>
            </TabItem>
          ))}
        </TabList>
      </TabsContainer>
    </>
  );
}
